import api, { BaseAPIClient, PaginatedResponse, QueryFilters, UrlOptions } from './api.ts';
import { APIError } from './errors.ts';
import { resourceInvalidationKeys } from './invalidation.ts';
import { EventAnalytics, ScheduledEmail } from './models.ts';
import { toInfiniteQuery } from './pagination.ts';
import { isUuid } from './utils.ts';

type ReadClient<T> = Pick<BaseAPIClient<T>, 'resource' | 'list' | 'get' | 'search'>;

const generatePaginatedListQuery =
    <T extends { id: string }>(resourceClient: ReadClient<T>) =>
    (filters: QueryFilters = {}, options?: UrlOptions) => ({
        queryKey: [resourceClient.resource, 'page', filters, options],
        queryFn: async ({
            pageParam,
            signal,
        }: {
            pageParam?: unknown;
            signal?: AbortSignal;
        }): Promise<PaginatedResponse<T>> => {
            console.debug(
                `fetch ${resourceClient.resource} paginated list, filters: ${JSON.stringify(filters)}, pageParam: ${pageParam}, options: ${options}`
            );
            return resourceClient.list(
                filters,
                {
                    ...options,
                    page:
                        typeof pageParam === 'number'
                            ? pageParam
                            : (options?.page ?? (typeof filters.page === 'number' ? filters.page : undefined)),
                },
                signal
            );
        },
        // disabled if any filter is undefined
        enabled: filters ? !Object.values(filters).some((v) => v === undefined) : true,
        staleTime: 1000, // 1 second
    });

const generateListQuery =
    <T extends { id: string }>(resourceClient: ReadClient<T>) =>
    (filters: QueryFilters = {}, options?: UrlOptions) => ({
        queryKey: [resourceClient.resource, 'list', filters, options],
        queryFn: async ({ signal }: { signal?: AbortSignal } = {}): Promise<T[]> => {
            console.debug(
                `fetch ${resourceClient.resource} list, filters: ${JSON.stringify(filters)}, options: ${options}`
            );
            const data = await resourceClient.list(filters, options, signal);
            return Array.isArray(data) ? data : data.results;
        },
        // disabled if any filter is undefined
        enabled: filters ? !Object.values(filters).some((v) => v === undefined) : true,
        staleTime: 1000, // 1 second
    });

const generateSearchQuery =
    <T extends { id: string }>(resourceClient: ReadClient<T>) =>
    (filters: QueryFilters = {}, options?: UrlOptions) => ({
        queryKey: [resourceClient.resource, 'search', filters, options],
        queryFn: async ({ signal }: { signal?: AbortSignal } = {}): Promise<PaginatedResponse<T>> => {
            console.debug(
                `fetch ${resourceClient.resource} search, filters: ${JSON.stringify(filters)}, options: ${options}`
            );
            return resourceClient.search(filters, options, signal);
        },
        // disabled if any filter is undefined
        enabled: filters ? !Object.values(filters).some((v) => v === undefined) : true,
    });

const generateDetailQuery =
    <T extends { id: string }>(resourceClient: ReadClient<T>) =>
    (id: string, options?: UrlOptions) => ({
        queryKey: [resourceClient.resource, 'detail', id, options],
        queryFn: async ({ signal }: { signal?: AbortSignal } = {}): Promise<T> => {
            console.debug(`fetch ${resourceClient.resource} detail`, id, options);
            return resourceClient.get(id, options, signal);
        },
        enabled: !!id,
        staleTime: 1000, // 1 second
    });

const generateLimitsQuery =
    <T extends {}>(resourceClient: { resource: string; getLimits: (id: string) => Promise<T> }) =>
    (id: string, options?: UrlOptions) => ({
        queryKey: [resourceClient.resource, 'limits', id, options],
        queryFn: async ({ signal }: { signal?: AbortSignal } = {}): Promise<T> => {
            console.debug(`fetch ${resourceClient.resource} limits`, id, options);
            const item = await resourceClient.getLimits(id);
            return item;
        },
        enabled: !!id,
        staleTime: 1000, // 1 second
    });

// Only optional viewer context treats these endpoint-specific statuses as absence.
const generateMeDetailQuery =
    <T extends { id: string }>(resourceClient: ReadClient<T>, missingStatuses: number[] = []) =>
    (id: string = 'me', options?: UrlOptions) => ({
        ...generateDetailQuery(resourceClient)(id, options),
        queryFn: async ({ signal }: { signal?: AbortSignal } = {}): Promise<T | null> => {
            try {
                const item = await resourceClient.get(id, options, signal);
                return id === 'me' && !item?.id ? null : item;
            } catch (error) {
                if (id === 'me' && error instanceof APIError && missingStatuses.includes(error.status!)) {
                    return null;
                }
                throw error;
            }
        },
        staleTime: Infinity,
    });

const generateCreateMutation =
    <T extends { id: string }, Input>(resourceClient: BaseAPIClient<T, Input>) =>
    () => ({
        mutationKey: [resourceClient.resource, 'create'],
        meta: { invalidates: resourceInvalidationKeys(resourceClient.resource, 'create') },
        mutationFn: async ({ data, ...options }: { data: Input } & UrlOptions) => {
            console.debug(`create ${resourceClient.resource}`, data, options);
            try {
                return await resourceClient.create(data, options);
            } catch (e) {
                console.error(e);
                throw new APIError('Failed to create resource', e);
            }
        },
    });

const generateUpdateMutation =
    <T extends { id: string }, Input>(resourceClient: BaseAPIClient<T, Input>) =>
    () => ({
        mutationKey: [resourceClient.resource, 'update'],
        meta: { invalidates: resourceInvalidationKeys(resourceClient.resource, 'update') },
        mutationFn: async ({
            id,
            data,
            partial,
            ...options
        }: {
            id: string;
            data: Partial<Input>;
            partial?: boolean;
        } & UrlOptions) => {
            console.debug(`update ${resourceClient.resource}`, data);
            try {
                return await resourceClient.update(id, data, partial, options);
            } catch (e) {
                console.error(e);
                throw new APIError('Failed to update resource', e);
            }
        },
    });

const generateDeleteMutation =
    <T extends { id: string }, Input>(resourceClient: BaseAPIClient<T, Input>) =>
    () => ({
        mutationKey: [resourceClient.resource, 'delete'],
        meta: { invalidates: resourceInvalidationKeys(resourceClient.resource, 'delete') },
        mutationFn: async ({ id, ...options }: { id: string } & UrlOptions) => {
            console.debug(`delete ${resourceClient.resource}`, id);
            try {
                return await resourceClient.delete(id, options);
            } catch (e) {
                console.error(e);
                throw new APIError('Failed to delete resource', e);
            }
        },
    });

// SPACES

export const spaceListQuery = generateListQuery(api.spaces);
export const spaceDetailQuery = generateMeDetailQuery(api.spaces);
export const spaceUpdateMutation = generateUpdateMutation(api.spaces);

export const spaceLimitsQuery = generateLimitsQuery(api.spaces);

// JOBS

export const jobListQuery = generateListQuery(api.jobs);
export const jobDetailQuery = generateDetailQuery(api.jobs);
export const jobCreateMutation = generateCreateMutation(api.jobs);
export const jobUpdateMutation = generateUpdateMutation(api.jobs);
export const jobDeleteMutation = generateDeleteMutation(api.jobs);

// USERS

export const userListQuery = generateListQuery(api.users);
export const userPaginatedListQuery = generatePaginatedListQuery(api.users);
export const userDetailQuery = generateMeDetailQuery(api.users, [401, 403, 404]);
export const userCreateMutation = generateCreateMutation(api.users);
export const userUpdateMutation = generateUpdateMutation(api.users);
export const userDeleteMutation = generateDeleteMutation(api.users);

export const userResendInvitationMutation = () => ({
    mutationKey: ['users', 'resend-invitation'],
    meta: { invalidates: [['users']] },
    mutationFn: async ({ id }: { id: string }) => {
        try {
            return await api.users.resendInvitation(id);
        } catch (e) {
            console.error(e);
            throw new APIError('Failed to resend invitation', e);
        }
    },
});

export const userResendConfirmEmailMutation = () => ({
    mutationKey: ['users', 'resend-confirm-email'],
    meta: { invalidates: [['users']] },
    mutationFn: async ({ id }: { id: string }) => {
        try {
            return await api.users.resendConfirmEmail(id);
        } catch (e) {
            console.error(e);
            throw new APIError('Failed to resend confirm email', e);
        }
    },
});

export const userUpdatePasswordMutation = () => ({
    mutationKey: ['users', 'update-password'],
    meta: { invalidates: [['users']] },
    mutationFn: async ({
        id,
        data,
    }: {
        id: string;
        data: { oldPassword: string; newPassword: string; confirmPassword: string };
    }) => {
        return await api.users.updatePassword(id, data);
    },
});

// EDUCATIONS

export const userEducationListQuery = generateListQuery(api.educations);
export const userEducationDetailQuery = generateDetailQuery(api.educations);
export const userEducationCreateMutation = generateCreateMutation(api.educations);
export const userEducationUpdateMutation = generateUpdateMutation(api.educations);
export const userEducationDeleteMutation = generateDeleteMutation(api.educations);

// EXPERIENCES

export const userExperienceListQuery = generateListQuery(api.experiences);
export const userExperienceDetailQuery = generateDetailQuery(api.experiences);
export const userExperienceCreateMutation = generateCreateMutation(api.experiences);
export const userExperienceUpdateMutation = generateUpdateMutation(api.experiences);
export const userExperienceDeleteMutation = generateDeleteMutation(api.experiences);

// USER NOTIFICATION SETTINGS

export const userNotificationSettingsListQuery = generateListQuery(api.userNotificationSettings);
export const userNotificationSettingsDetailQuery = generateDetailQuery(api.userNotificationSettings);
export const userNotificationSettingsCreateMutation = generateCreateMutation(api.userNotificationSettings);
export const userNotificationSettingsUpdateMutation = generateUpdateMutation(api.userNotificationSettings);
export const userNotificationSettingsDeleteMutation = generateDeleteMutation(api.userNotificationSettings);

// ORGANIZATIONS

export const organizationListQuery = generateListQuery(api.organizations);
export const organizationPaginatedListQuery = generatePaginatedListQuery(api.organizations);
export const organizationSearchQuery = generateSearchQuery(api.organizations);
export const organizationDetailQuery = generateMeDetailQuery(api.organizations, [401, 403, 404]);
export const organizationCreateMutation = generateCreateMutation(api.organizations);
export const organizationUpdateMutation = generateUpdateMutation(api.organizations);
export const organizationDeleteMutation = generateDeleteMutation(api.organizations);

export const organizationLimitsQuery = generateLimitsQuery(api.organizations);

// ORGANIZATION PROFILES

export const organizationProfileListQuery = generateListQuery(api.organizationProfiles);
export const organizationProfilePaginatedListQuery = generatePaginatedListQuery(api.organizationProfiles);
export const organizationProfileSearchQuery = generateSearchQuery(api.organizationProfiles);
export const organizationProfileDetailQuery = generateDetailQuery(api.organizationProfiles);
export const organizationProfileCreateMutation = generateCreateMutation(api.organizationProfiles);
export const organizationProfileUpdateMutation = generateUpdateMutation(api.organizationProfiles);
export const organizationProfileDeleteMutation = generateDeleteMutation(api.organizationProfiles);

export const organizationProfileLimitsQuery = generateLimitsQuery(api.organizationProfiles);

// USER PROFILE

export const userProfileListQuery = generateListQuery(api.userProfiles);
export const userProfileSearchQuery = generateSearchQuery(api.userProfiles);
export const userProfileDetailQuery = generateMeDetailQuery(api.userProfiles, [401, 403, 404]);
export const userProfileCreateMutation = generateCreateMutation(api.userProfiles);
export const userProfileUpdateMutation = generateUpdateMutation(api.userProfiles);
export const userProfileDeleteMutation = generateDeleteMutation(api.userProfiles);

// USER NOTES

export const userNoteListQuery = generateListQuery(api.userNotes);
export const userNoteDetailQuery = generateDetailQuery(api.userNotes);
export const userNoteCreateMutation = generateCreateMutation(api.userNotes);
export const userNoteUpdateMutation = generateUpdateMutation(api.userNotes);
export const userNoteDeleteMutation = generateDeleteMutation(api.userNotes);

// EVENTS

export const eventListQuery = generateListQuery(api.events);
export const eventDetailQuery = (id: string = 'me', options?: UrlOptions) => ({
    ...(isUuid(id) || id === 'me'
        ? generateMeDetailQuery(api.events, [404])(id, options)
        : {
              queryKey: ['events', 'detail', id, options],
              queryFn: async () => {
                  const events = await generateListQuery(api.events)({ slug: id }, options).queryFn();
                  if (!events.length) throw new APIError('Event not found', undefined, 404);
                  return events[0];
              },
          }),
    staleTime: Infinity,
});

export const eventCreateMutation = generateCreateMutation(api.events);
export const eventUpdateMutation = generateUpdateMutation(api.events);
export const eventDeleteMutation = generateDeleteMutation(api.events);

export const eventLimitsQuery = generateLimitsQuery(api.events);

export const eventAnalyticsQuery = (id: string) => ({
    queryKey: ['events', 'analytics', id],
    queryFn: async () => {
        console.debug(`fetch ${api.events.resource} analytics`, id);
        const item = await api.events.getAnalytics(id);
        return item as EventAnalytics;
    },
    staleTime: Infinity,
});

// EVENT USER

export const eventAttendeeListQuery = generateListQuery(api.eventAttendees);
export const eventAttendeePaginatedListQuery = generatePaginatedListQuery(api.eventAttendees);
export const eventAttendeeDetailQuery = generateDetailQuery(api.eventAttendees);
export const eventAttendeeCreateMutation = generateCreateMutation(api.eventAttendees);
export const eventAttendeeUpdateMutation = generateUpdateMutation(api.eventAttendees);
export const eventAttendeeDeleteMutation = generateDeleteMutation(api.eventAttendees);

// EVENT SESSION

export const eventSessionListQuery = generateListQuery(api.eventSessions);
export const eventSessionSearchQuery = generateSearchQuery(api.eventSessions);
export const eventSessionDetailQuery = generateDetailQuery(api.eventSessions);
export const eventSessionCreateMutation = generateCreateMutation(api.eventSessions);
export const eventSessionUpdateMutation = generateUpdateMutation(api.eventSessions);
export const eventSessionDeleteMutation = generateDeleteMutation(api.eventSessions);

// EVENT SESSION USER

export const eventSessionUserListQuery = generateListQuery(api.eventSessionUsers);
export const eventSessionUserDetailQuery = generateDetailQuery(api.eventSessionUsers);
export const eventSessionUserCreateMutation = generateCreateMutation(api.eventSessionUsers);
export const eventSessionUserUpdateMutation = generateUpdateMutation(api.eventSessionUsers);
export const eventSessionUserDeleteMutation = generateDeleteMutation(api.eventSessionUsers);

// EVENT PAGES

export const eventPageListQuery = generateListQuery(api.eventPages);
export const eventPageDetailQuery = generateDetailQuery(api.eventPages);
export const eventPageCreateMutation = generateCreateMutation(api.eventPages);
export const eventPageUpdateMutation = generateUpdateMutation(api.eventPages);
export const eventPageDeleteMutation = generateDeleteMutation(api.eventPages);

// ORGANIZATION PROFILE JOBS

export const organizationProfileJobListQuery = generateListQuery(api.organizationProfileJobs);
export const organizationProfileJobSearchQuery = generateSearchQuery(api.organizationProfileJobs);
export const organizationProfileJobDetailQuery = generateDetailQuery(api.organizationProfileJobs);
export const organizationProfileJobCreateMutation = generateCreateMutation(api.organizationProfileJobs);
export const organizationProfileJobUpdateMutation = generateUpdateMutation(api.organizationProfileJobs);
export const organizationProfileJobDeleteMutation = generateDeleteMutation(api.organizationProfileJobs);

// ORGANIZATION PROFILE USERS

export const organizationProfileUserListQuery = generateListQuery(api.organizationProfileUsers);
export const organizationProfileUserDetailQuery = generateDetailQuery(api.organizationProfileUsers);
export const organizationProfileUserCreateMutation = generateCreateMutation(api.organizationProfileUsers);
export const organizationProfileUserUpdateMutation = generateUpdateMutation(api.organizationProfileUsers);
export const organizationProfileUserDeleteMutation = generateDeleteMutation(api.organizationProfileUsers);

// CONVERSATIONS

export const conversationListQuery = generateListQuery(api.conversations);
export const conversationPaginatedListQuery = generatePaginatedListQuery(api.conversations);
export const conversationInfiniteListQuery = (filters: QueryFilters = {}, options?: UrlOptions) =>
    toInfiniteQuery(conversationPaginatedListQuery(filters, options));
export const conversationDetailQuery = generateDetailQuery(api.conversations);
export const conversationCreateMutation = generateCreateMutation(api.conversations);
export const conversationUpdateMutation = generateUpdateMutation(api.conversations);
export const conversationDeleteMutation = generateDeleteMutation(api.conversations);

export const conversationMarkReadMutation = () => ({
    mutationKey: ['conversations', 'mark-read'],
    meta: { invalidates: [['conversations']] },
    mutationFn: async ({ id }: { id: string }) => {
        try {
            return await api.conversations.markRead(id);
        } catch (e) {
            console.error(e);
            throw new APIError('Failed to mark conversation as read', e);
        }
    },
});

export const conversationReportMutation = () => ({
    mutationKey: ['conversations', 'report'],
    meta: { invalidates: resourceInvalidationKeys('conversations') },
    mutationFn: async ({ id, data }: { id: string; data: { targetUserId: string } }) => {
        try {
            return await api.conversations.report(id, data);
        } catch (e) {
            console.error(e);
            throw new APIError('Failed to report', e);
        }
    },
});

export const conversationBlockMutation = () => ({
    mutationKey: ['conversations', 'block'],
    meta: { invalidates: resourceInvalidationKeys('conversations') },
    mutationFn: async ({ id }: { id: string }) => {
        try {
            return await api.conversations.block(id);
        } catch (e) {
            console.error(e);
            throw new APIError('Failed to block', e);
        }
    },
});

export const conversationUnblockMutation = () => ({
    mutationKey: ['conversations', 'unblock'],
    meta: { invalidates: resourceInvalidationKeys('conversations') },
    mutationFn: async ({ id }: { id: string }) => {
        try {
            return await api.conversations.unblock(id);
        } catch (e) {
            console.error(e);
            throw new APIError('Failed to unblock', e);
        }
    },
});

// MESSAGES

export const messageListQuery = generateListQuery(api.messages);
export const messagePaginatedListQuery = generatePaginatedListQuery(api.messages);
export const messageInfiniteListQuery = (filters: QueryFilters = {}, options?: UrlOptions) =>
    toInfiniteQuery(messagePaginatedListQuery(filters, options));
export const messageDetailQuery = generateDetailQuery(api.messages);
export const messageCreateMutation = generateCreateMutation(api.messages);
export const messageUpdateMutation = generateUpdateMutation(api.messages);
export const messageDeleteMutation = generateDeleteMutation(api.messages);

// SAVED USERS

export const savedUserListQuery = generateListQuery(api.savedUsers);
export const savedUserDetailQuery = generateDetailQuery(api.savedUsers);
export const savedUserCreateMutation = generateCreateMutation(api.savedUsers);
export const savedUserUpdateMutation = generateUpdateMutation(api.savedUsers);
export const savedUserDeleteMutation = generateDeleteMutation(api.savedUsers);

// SAVED ORGANIZATIONS

export const savedOrganizationListQuery = generateListQuery(api.savedOrganizations);
export const savedOrganizationDetailQuery = generateDetailQuery(api.savedOrganizations);
export const savedOrganizationCreateMutation = generateCreateMutation(api.savedOrganizations);
export const savedOrganizationUpdateMutation = generateUpdateMutation(api.savedOrganizations);
export const savedOrganizationDeleteMutation = generateDeleteMutation(api.savedOrganizations);

// SAVED JOBS

export const savedJobListQuery = generateListQuery(api.savedJobs);
export const savedJobDetailQuery = generateDetailQuery(api.savedJobs);
export const savedJobCreateMutation = generateCreateMutation(api.savedJobs);
export const savedJobUpdateMutation = generateUpdateMutation(api.savedJobs);
export const savedJobDeleteMutation = generateDeleteMutation(api.savedJobs);

// SAVED EVENT SESSIONS

export const savedEventSessionListQuery = generateListQuery(api.savedEventSessions);
export const savedEventSessionDetailQuery = generateDetailQuery(api.savedEventSessions);
export const savedEventSessionCreateMutation = generateCreateMutation(api.savedEventSessions);
export const savedEventSessionUpdateMutation = generateUpdateMutation(api.savedEventSessions);
export const savedEventSessionDeleteMutation = generateDeleteMutation(api.savedEventSessions);

// Files

export const fileListQuery = generateListQuery(api.files);
export const fileSearchQuery = generateSearchQuery(api.files);
export const fileDetailQuery = generateDetailQuery(api.files);
export const fileCreateMutation = generateCreateMutation(api.files);
export const fileUpdateMutation = generateUpdateMutation(api.files);
export const fileDeleteMutation = generateDeleteMutation(api.files);

// SCHEDULED EMAILS

export const scheduledEmailListQuery = generateListQuery(api.scheduledEmails);
export const scheduledEmailSearchQuery = generateSearchQuery(api.scheduledEmails);
export const scheduledEmailDetailQuery = generateDetailQuery(api.scheduledEmails);
export const scheduledEmailCreateMutation = generateCreateMutation(api.scheduledEmails);
export const scheduledEmailUpdateMutation = generateUpdateMutation(api.scheduledEmails);
export const scheduledEmailDeleteMutation = generateDeleteMutation(api.scheduledEmails);

export const scheduledEmailPreviewMutation = () => ({
    mutationKey: ['scheduled-email', 'preview'],
    mutationFn: async ({ data }: { data: Partial<ScheduledEmail> }) => {
        try {
            return await api.scheduledEmails.preview(data);
        } catch (e) {
            console.error(e);
            throw new APIError('Failed to generate preview', e);
        }
    },
});

export const scheduledEmailSendPreviewMutation = () => ({
    mutationKey: ['scheduled-email', 'send-preview'],
    mutationFn: async ({ data }: { data: Partial<ScheduledEmail> }) => {
        try {
            return await api.scheduledEmails.sendPreview(data);
        } catch (e) {
            console.error(e);
            throw new APIError('Failed to send preview', e);
        }
    },
});

// SUGGESTIONS

export const suggestionPaginatedListQuery = generatePaginatedListQuery(api.suggestions);
export const suggestionListQuery = generateListQuery(api.suggestions);

// LOG EVENTS

export const logEventCreateMutation = () => ({
    ...generateCreateMutation(api.logEvents)(),
    meta: { skipInvalidateQueries: true },
});

export const logEventAcceptTrackingMutation = () => ({
    mutationKey: ['log-event', 'accept-tracking'],
    meta: { invalidates: [['log-event', 'tracking-status']] },
    mutationFn: async () => {
        try {
            return await api.logEvents.acceptTracking();
        } catch (e) {
            console.error(e);
            throw new APIError('Failed to accept tracking', e);
        }
    },
});

export const logEventRejectTrackingMutation = () => ({
    mutationKey: ['log-event', 'reject-tracking'],
    meta: { invalidates: [['log-event', 'tracking-status']] },
    mutationFn: async () => {
        try {
            return await api.logEvents.rejectTracking();
        } catch (e) {
            console.error(e);
            throw new APIError('Failed to reject tracking', e);
        }
    },
});

export const logEventTrackingStatusQuery = () => ({
    queryKey: ['log-event', 'tracking-status'],
    queryFn: async () => {
        try {
            return await api.logEvents.trackingStatus();
        } catch (e) {
            console.error(e);
            throw new APIError('Failed to get tracking status', e);
        }
    },
    staleTime: Infinity,
});
